<template>
  <main>
    <div class="px-4 sm:px-6 lg:px-8 mt-10">
      <div class="sm:flex sm:items-center sm:justify-between">
        <h1 class="text-base font-semibold leading-6 text-gray-900">Private Release Invitations</h1>
        <button
          @click="reload()"
          :disabled="loading"
          type="button"
          class="rounded bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-25"
        >
          {{ loading ? 'Loading…' : 'Refresh' }}
        </button>
      </div>

      <!-- Stat tiles (server-side aggregation counts, independent of pagination) -->
      <div class="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Total Tracked</p>
          <p class="mt-1 text-2xl font-semibold text-gray-900">{{ counts.total }}</p>
        </div>
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Viewed</p>
          <p class="mt-1 text-2xl font-semibold text-gray-500">{{ counts.viewed }}</p>
        </div>
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Accepted</p>
          <p class="mt-1 text-2xl font-semibold text-green-600">{{ counts.accepted }}</p>
        </div>
        <div class="rounded-lg bg-white px-4 py-5 shadow ring-1 ring-black ring-opacity-5">
          <p class="text-sm text-gray-500">Declined</p>
          <p class="mt-1 text-2xl font-semibold text-red-600">{{ counts.declined }}</p>
        </div>
      </div>

      <!-- Filters (client-side, scoped to loaded rows only) -->
      <div class="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div class="flex space-x-2">
          <button
            v-for="option in statusOptions"
            :key="option.value"
            @click="statusFilter = option.value"
            type="button"
            :class="[
              statusFilter === option.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
              'rounded px-3 py-1.5 text-xs font-semibold'
            ]"
          >
            {{ option.label }}
          </button>
        </div>
        <input
          v-model="search"
          type="text"
          placeholder="Search by email or uid"
          class="flex-1 max-w-xs rounded-md border-gray-300 shadow-sm text-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        <p class="text-xs text-gray-400">Filter/search applies to loaded rows only — Load more to search further.</p>
      </div>

      <!-- Table -->
      <div class="mt-4 flow-root">
        <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Email</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">UID</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Viewed At</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Responded At</th>
                    <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">User Agent</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white">
                  <tr v-for="invitation in filteredInvitations" :key="invitation.id">
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{{ invitation.email || 'NA' }}</td>
                    <td class="whitespace-nowrap px-3 py-4 text-xs text-gray-500">{{ invitation.id }}</td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm">
                      <span :class="['badge', statusBadgeClass(invitation.status)]">{{ invitation.status }}</span>
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ formatDate(invitation.viewedAt) }}</td>
                    <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ formatDate(invitation.respondedAt) }}</td>
                    <td class="px-3 py-4 text-xs text-gray-500 max-w-xs truncate" :title="invitation.userAgent || ''">{{ invitation.userAgent || 'NA' }}</td>
                  </tr>
                  <tr v-if="!loading && filteredInvitations.length === 0">
                    <td colspan="6" class="px-3 py-8 text-center text-sm text-gray-500">No invitations match this filter.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="my-4 text-center">
        <div v-if="invitations.length > 0" class="mb-3 text-xs text-gray-500">
          Showing {{ filteredInvitations.length }} of {{ invitations.length }} loaded (of {{ counts.total }} total)
        </div>
        <button
          v-if="hasMore"
          @click="loadMore()"
          :disabled="loading"
          type="button"
          class="mx-auto block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-30"
        >
          {{ loading ? 'Loading…' : 'Load more' }}
        </button>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { db } from '@/firebase/config.js'
import { getDocs, getCountFromServer, collection, query, orderBy, startAfter, limit, where } from 'firebase/firestore'

const PAGE_SIZE = 100

const invitations = ref([])
const lastDoc = ref(null)
const hasMore = ref(true)
const loading = ref(false)
const statusFilter = ref('all')
const search = ref('')
const counts = ref({ total: 0, viewed: 0, accepted: 0, declined: 0 })

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'viewed', label: 'Viewed' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
]

const filteredInvitations = computed(() => {
  const term = search.value.trim().toLowerCase()
  return invitations.value.filter(invitation => {
    const matchesStatus = statusFilter.value === 'all' || invitation.status === statusFilter.value
    const matchesSearch = !term
      || (invitation.email && invitation.email.toLowerCase().includes(term))
      || (invitation.id && invitation.id.toLowerCase().includes(term))
    return matchesStatus && matchesSearch
  })
})

const statusBadgeClass = (status) => {
  switch (status) {
    case 'accepted': return 'bg-green-50 text-green-700 ring-green-600/20'
    case 'declined': return 'bg-red-50 text-red-700 ring-red-600/20'
    default: return 'bg-gray-50 text-gray-600 ring-gray-500/20'
  }
}

const formatDate = (timestamp) => {
  if (!timestamp || typeof timestamp.toDate !== 'function') return '—'
  return timestamp.toDate().toLocaleString()
}

const loadCounts = async () => {
  const invitationsRef = collection(db, 'private_release_invitations')
  const [total, viewed, accepted, declined] = await Promise.all([
    getCountFromServer(invitationsRef),
    getCountFromServer(query(invitationsRef, where('status', '==', 'viewed'))),
    getCountFromServer(query(invitationsRef, where('status', '==', 'accepted'))),
    getCountFromServer(query(invitationsRef, where('status', '==', 'declined'))),
  ])
  counts.value = {
    total: total.data().count,
    viewed: viewed.data().count,
    accepted: accepted.data().count,
    declined: declined.data().count,
  }
}

const loadMore = async () => {
  loading.value = true
  try {
    const invitationsRef = collection(db, 'private_release_invitations')
    const baseQuery = lastDoc.value
      ? query(invitationsRef, orderBy('viewedAt', 'desc'), startAfter(lastDoc.value), limit(PAGE_SIZE))
      : query(invitationsRef, orderBy('viewedAt', 'desc'), limit(PAGE_SIZE))

    const snapshot = await getDocs(baseQuery)
    invitations.value.push(...snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })))
    lastDoc.value = snapshot.docs[snapshot.docs.length - 1] || null
    hasMore.value = snapshot.docs.length === PAGE_SIZE
  } catch (error) {
    console.error('Error loading private_release_invitations:', error)
  } finally {
    loading.value = false
  }
}

const reload = async () => {
  invitations.value = []
  lastDoc.value = null
  hasMore.value = true
  await Promise.all([loadCounts(), loadMore()])
}

onMounted(reload)
</script>

<style scoped>
@layer components {
  main {
    @apply h-screen overflow-auto
  }

  .badge {
    @apply inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset capitalize
  }
}
</style>
